import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { MATCH_AUTO, MATCH_NONE } from '../../constants/matching-algorithms'
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination'
import { useSetting } from '../../hooks/useSettings'
import { documentListViewService } from '../../services/document-list-view.service'
import { PermissionAction, PermissionType, permissionsService } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { FilterRule } from '../../types'
import { getMatchingAlgorithms } from '../../utils/matchingAlgorithms'
import IfPermissions from './IfPermissions'
import './ManagementList.scss'
import PageHeader from './PageHeader'
import { Pagination } from './Pagination'
import { PermissionsDialog, PermissionsFormData } from './PermissionsDialog'

const FILTER_CORRESPONDENT = 3
const FILTER_DOCUMENT_TYPE = 4
const FILTER_STORAGE_PATH = 25
const FILTER_HAS_TAGS_ALL = 6
const FILTER_WORKFLOW_STATUS = 48

export interface ManagementListColumn {
  key: string
  name: string
  valueFn: (item: any) => any
  rendersHtml?: boolean
  hideOnMobile?: boolean
  monospace?: boolean
}

/** Optional render prop so parents can place actions that depend on checkbox selection (same source as Delete / Permissions). */
export type ManagementListHeaderActionsRender = (selectedIds: Set<number>) => React.ReactNode

export interface ManagementListProps<T extends { name: string }> {
  service: any
  editDialogComponent: React.ComponentType<any>
  typeName: string
  typeNamePlural: string
  permissionType: string
  extraColumns?: ManagementListColumn[]
  filterData?: (data: T[], nameFilter?: string) => T[]
  getDeleteMessage: (object: T) => string
  pageSize?: number
  info?: string
  infoLink?: string
  /** Extra header controls: static node, or a function of current row selection (checkboxes). */
  headerActions?: React.ReactNode | ManagementListHeaderActionsRender
  onExtraAction?: (object: T) => void
  extraActionTitle?: string
  extraActionIcon?: string
  /** Hide matching algorithm/pattern column (for workflow_status etc.) */
  hideMatchingColumn?: boolean
  /** Hide permissions button (for workflow_status etc.) */
  hidePermissionsButton?: boolean
  documentCountValueFn?: (object: T) => React.ReactNode
  isDocumentCountDisabled?: (object: T) => boolean
}

const LEGACY_PERMISSION_TYPE_MAP: Record<string, PermissionType> = {
  correspondent: PermissionType.Correspondent,
  tag: PermissionType.Tag,
  custom_field: PermissionType.CustomField,
  document_type: PermissionType.DocumentType,
  workflow_status: PermissionType.WorkflowStatus,
  storage_path: PermissionType.StoragePath,
  storage_paths: PermissionType.StoragePath,
}

const resolvePermissionType = (permissionType: string): PermissionType | null => {
  if ((Object.values(PermissionType) as string[]).includes(permissionType)) {
    return permissionType as PermissionType
  }
  return LEGACY_PERMISSION_TYPE_MAP[permissionType] || null
}

interface ManagementTableProps {
  data: any[]
  extraColumns: ManagementListColumn[]
  sortField: string
  sortReverse: boolean
  onSort: (column: string) => void
  selectedObjects: Set<number>
  toggleAll: boolean
  onToggleAll: () => void
  onToggleSelected: (object: any) => void
  onFilterDocuments: (object: any) => void
  onEdit: (object: any) => void
  onDelete: (object: any) => void
  onClone?: (object: any) => void
  onExtraAction?: (object: any) => void
  extraActionTitle?: string
  extraActionIcon?: string
  getDocumentCount: (object: any) => number
  documentCountValueFn?: (object: any) => React.ReactNode
  isDocumentCountDisabled?: (object: any) => boolean
  getMatching: (object: any) => string
  permissionType: PermissionType | null
  t: (key: string, options?: any) => string
  emptyLabel: string
  hideMatchingColumn?: boolean
}

const ManagementTable = memo(
  ({
    data,
    extraColumns,
    sortField,
    sortReverse,
    onSort,
    selectedObjects,
    toggleAll,
    onToggleAll,
    onToggleSelected,
    onFilterDocuments,
    onEdit,
    onDelete,
    onClone,
    onExtraAction,
    extraActionTitle,
    extraActionIcon,
    getDocumentCount,
    documentCountValueFn,
    isDocumentCountDisabled,
    getMatching,
    permissionType,
    t,
    emptyLabel,
    hideMatchingColumn,
  }: ManagementTableProps) => (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>
              <div className="form-check m-0 ms-2 me-n2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="all-objects"
                  checked={toggleAll}
                  onChange={onToggleAll}
                  disabled={data.length === 0}
                />
                <label className="form-check-label" htmlFor="all-objects"></label>
              </div>
            </th>
            <th className="sortable" onClick={() => onSort('name')}>
              {t('name')}
              {sortField === 'name' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
            </th>
            {!hideMatchingColumn && (
              <th className="sortable d-none d-sm-table-cell" onClick={() => onSort('matching_algorithm')}>
                {t('matching')}
                {sortField === 'matching_algorithm' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
              </th>
            )}
            <th className="sortable" onClick={() => onSort('document_count')}>
              {t('document_count')}
              {sortField === 'document_count' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
            </th>
            {extraColumns.map((column) => (
              <th key={column.key} className={`${column.hideOnMobile ? 'd-none d-sm-table-cell' : ''} sortable`} onClick={() => onSort(column.key)}>
                {column.name}
                {sortField === column.key && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
              </th>
            ))}
            <th className="text-end">{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={5 + (hideMatchingColumn ? 0 : 1) + extraColumns.length} className="text-center text-muted py-4">
                <i className="bi bi-info-circle me-2"></i>
                {t('no_items_found')} {emptyLabel}
              </td>
            </tr>
          ) : (
            (() => {
              const renderObjectRow = (obj: any, depth: number): React.ReactNode => {
                return (
                  <React.Fragment key={obj.id}>
                    <tr>
                      <td>
                        <div className="form-check m-0 ms-2 me-n2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`object-${obj.id}`}
                            checked={selectedObjects.has(obj.id)}
                            onChange={() => onToggleSelected(obj)}
                          />
                          <label className="form-check-label" htmlFor={`object-${obj.id}`}></label>
                        </div>
                      </td>
                      <td className="name-cell" style={{ '--depth': depth + 1 } as React.CSSProperties}>
                        {depth > 0 && <div className="indicator"></div>}
                        <button
                          className="btn btn-link p-0 text-start"
                          onClick={() => onEdit(obj)}
                          disabled={!permissionType || !permissionsService.currentUserCan(PermissionAction.Change, permissionType)}
                        >
                          {obj.name}
                        </button>
                      </td>
                      {!hideMatchingColumn && <td className="d-none d-sm-table-cell">{getMatching(obj)}</td>}
                      <td>
                        <button
                          className="btn btn-link p-0 text-start"
                          onClick={() => onFilterDocuments(obj)}
                          disabled={
                            isDocumentCountDisabled
                              ? isDocumentCountDisabled(obj)
                              : !obj.document_count || obj.document_count === 0
                          }
                        >
                          {documentCountValueFn ? documentCountValueFn(obj) : getDocumentCount(obj)}
                        </button>
                      </td>
                      {extraColumns.map((column) => (
                        <td key={column.key} className={column.hideOnMobile ? 'd-none d-sm-table-cell' : ''}>
                          {column.rendersHtml ? (
                            <div dangerouslySetInnerHTML={{ __html: column.valueFn(obj) }} />
                          ) : column.monospace ? (
                            <span className="font-monospace">{column.valueFn(obj)}</span>
                          ) : (
                            column.valueFn(obj)
                          )}
                        </td>
                      ))}
                      <td className="text-end">
                        <div className="btn-group">
                          {permissionType && <IfPermissions action={PermissionAction.Change} type={permissionType}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => onEdit(obj)} title={t('edit')}>
                              <i className="bi bi-pencil"></i>
                            </button>
                          </IfPermissions>}
                          {onExtraAction && (
                            permissionType && <IfPermissions action={PermissionAction.Change} type={permissionType}>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => onExtraAction(obj)}
                                title={extraActionTitle || ''}
                              >
                                <i className={extraActionIcon || 'bi bi-file-earmark-text'}></i>
                              </button>
                            </IfPermissions>
                          )}
                          {(permissionType === PermissionType.CustomField || permissionType === PermissionType.DocumentType) && (
                            <IfPermissions action={PermissionAction.Add} type={permissionType}>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => onClone?.(obj)}
                                title={permissionType === PermissionType.DocumentType ? t('copy') : t('clone') || 'Clone'}
                              >
                                <i className="bi bi-files"></i>
                              </button>
                            </IfPermissions>
                          )}
                          {permissionType && <IfPermissions action={PermissionAction.Delete} type={permissionType}>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(obj)} title={t('delete')}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </IfPermissions>}
                        </div>
                      </td>
                    </tr>
                    {obj.children && obj.children.length > 0 && <>{obj.children.map((child: any) => renderObjectRow(child, depth + 1))}</>}
                  </React.Fragment>
                )
              }

              return data.map((object: any) => renderObjectRow(object, 0))
            })()
          )}
        </tbody>
      </table>
    </div>
  )
)

export const ManagementList = <T extends { name: string }>({
  service,
  editDialogComponent: EditDialogComponent,
  typeName,
  typeNamePlural,
  permissionType,
  extraColumns = [],
  filterData: customFilterData,
  getDeleteMessage,
  pageSize = DEFAULT_PAGE_SIZE,
  info,
  infoLink,
  headerActions,
  onExtraAction,
  extraActionTitle,
  extraActionIcon,
  hideMatchingColumn = false,
  hidePermissionsButton = false,
  documentCountValueFn,
  isDocumentCountDisabled,
}: ManagementListProps<T>) => {
  const resolvedPermissionType = resolvePermissionType(permissionType)
  useEffect(() => {
    if (!resolvedPermissionType) {
      // Surface configuration mistakes early instead of silently hiding all actions.
      console.warn(`[ManagementList] Unknown permissionType "${permissionType}"`)
    }
  }, [permissionType, resolvedPermissionType])
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [data, setData] = useState<T[]>([])
  const [unfilteredData, setUnfilteredData] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [collectionSize, setCollectionSize] = useState(0)
  const [sortField, setSortField] = useState<string>('')
  const [sortReverse, setSortReverse] = useState(false)
  const [nameFilter, setNameFilter] = useState('')
  const [nameFilterInput, setNameFilterInput] = useState('')
  const nameFilterTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const filterInputRef = useRef<HTMLInputElement | null>(null)
  const maintainFilterFocusRef = useRef(false)
  const [selectedObjects, setSelectedObjects] = useState<Set<number>>(new Set())
  const [toggleAll, setToggleAll] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [objectToDelete, setObjectToDelete] = useState<T | null>(null)
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingObject, setEditingObject] = useState<T | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)
  const effectivePageSize = pageSize || DEFAULT_PAGE_SIZE
  const shouldShowPagination = collectionSize > effectivePageSize
  const totalPages = Math.max(1, Math.ceil(collectionSize / effectivePageSize))
  const rangeStart = collectionSize === 0 ? 0 : (page - 1) * effectivePageSize + 1
  const rangeEnd = Math.min(collectionSize, page * effectivePageSize)
  const [initialLoad, setInitialLoad] = useState(true)

  useEffect(() => {
    document.title = `${t(typeNamePlural)} - ${appTitle}`
  }, [typeNamePlural, t, appTitle])

  const loadData = useCallback(
    async (extraParams: any = null) => {
      setLoading(true)
      try {
        const baseParams = { page_size: effectivePageSize }
        const mergedParams = extraParams ? { ...baseParams, ...extraParams } : baseParams

        const response = await service.listFiltered(page, null, sortField, sortReverse, nameFilter, true, mergedParams)

        setUnfilteredData(response.results)
        const filteredData = customFilterData ? customFilterData(response.results, nameFilter) : response.results
        setData(filteredData)
        setCollectionSize(response.count)
      } catch (error) {
        toastService.showError(t('error_loading_data'))
      } finally {
        setLoading(false)
      }
    },
    [page, sortField, sortReverse, nameFilter, service, t, customFilterData, effectivePageSize]
  )

  useEffect(() => {
    ;(async () => {
      await loadData()
      setInitialLoad(false)
    })()
  }, [loadData])

  useEffect(() => {
    if (nameFilterInput === nameFilter) {
      return
    }

    const handler = setTimeout(() => {
      setNameFilter(nameFilterInput)
      setPage(1)
    }, 400)

    return () => clearTimeout(handler)
  }, [nameFilterInput, nameFilter])

  useEffect(() => {
    setNameFilterInput(nameFilter)
  }, [nameFilter])

  useEffect(() => {
    return () => {
      if (nameFilterTimeoutRef.current) {
        clearTimeout(nameFilterTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!loading && maintainFilterFocusRef.current && filterInputRef.current) {
      const input = filterInputRef.current
      input.focus()
      const length = input.value.length
      input.setSelectionRange(length, length)
    }
  }, [loading])

  const handleSort = useCallback(
    (column: string) => {
      setSortField((prevField) => {
        if (prevField !== column) {
          setSortReverse(false)
          return column
        } else if (prevField === column && !sortReverse) {
          setSortReverse(true)
          return prevField
        } else {
          setSortReverse(false)
          return ''
        }
      })
    },
    [sortReverse]
  )

  const handleNameFilterChange = (value: string) => {
    setNameFilterInput(value)

    if (nameFilterTimeoutRef.current) {
      clearTimeout(nameFilterTimeoutRef.current)
    }

    nameFilterTimeoutRef.current = setTimeout(() => {
      setNameFilter(value)
      setPage(1)
      nameFilterTimeoutRef.current = null
    }, 400)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleToggleAll = useCallback(() => {
    setSelectedObjects(() => {
      if (toggleAll) {
        return new Set()
      }
      return new Set(data.map((item: any) => item.id))
    })
    setToggleAll(!toggleAll)
  }, [toggleAll, data])

  const handleToggleSelected = useCallback((object: any) => {
    setSelectedObjects((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(object.id)) {
        newSelected.delete(object.id)
      } else {
        newSelected.add(object.id)
      }
      return newSelected
    })
  }, [])

  const clearSelection = () => {
    setSelectedObjects(new Set())
    setToggleAll(false)
  }

  const openCreateDialog = () => {
    setEditingObject(null)
    setIsCreating(true)
    setShowEditDialog(true)
  }

  const openEditDialog = useCallback((object: T) => {
    setEditingObject(object)
    setIsCreating(false)
    setShowEditDialog(true)
  }, [])

  const openCloneDialog = useCallback((object: T) => {
    const clonedObject =
      permissionType === 'document_type'
        ? ({
            ...object,
            id: undefined,
            name: `${(object as any).name} (Copy)`,
          } as T)
        : ({
            ...object,
            id: undefined,
            name: undefined,
          } as T)
    setEditingObject(clonedObject)
    setIsCreating(true)
    setShowEditDialog(true)
  }, [permissionType])

  const openDeleteDialog = useCallback((object: T) => {
    setObjectToDelete(object)
    setShowConfirmDialog(true)
  }, [])

  const openBulkDeleteDialog = () => {
    setShowBulkDeleteDialog(true)
  }

  const openPermissionsDialog = () => {
    setShowPermissionsDialog(true)
  }

  const handleFilterDocuments = useCallback(
    (object: any) => {
      if (!object.document_count || object.document_count === 0) return

      let ruleType: number

      switch (permissionType) {
        case 'tag':
          ruleType = FILTER_HAS_TAGS_ALL
          break
        case 'correspondent':
          ruleType = FILTER_CORRESPONDENT
          break
        case 'document_type':
          ruleType = FILTER_DOCUMENT_TYPE
          break
        case 'storage_paths':
        case 'storage_path':
          ruleType = FILTER_STORAGE_PATH
          break
        case 'workflow_status':
          ruleType = FILTER_WORKFLOW_STATUS
          break
        default:
          return
      }

      const filterRule: FilterRule = {
        rule_type: ruleType,
        value: object.id.toString(),
      }

      documentListViewService.selectNone()
      documentListViewService.quickFilter([filterRule])
      navigate('/documents')
    },
    [navigate, permissionType]
  )

  const handlePermissionsConfirm = async (permissions: PermissionsFormData, merge: boolean) => {
    try {
      await service.bulkEditObjects(Array.from(selectedObjects), 'set_permissions', permissions, merge)
      toastService.showInfo(t('permissions_updated_successfully'))
      setShowPermissionsDialog(false)
      loadData()
    } catch (error) {
      toastService.showError(t('error_updating_permissions'))
    }
  }

  const handleDelete = async () => {
    if (!objectToDelete) return

    try {
      await service.delete(objectToDelete)
      toastService.showInfo(t('successfully_deleted', { name: (objectToDelete as any).name }))
      loadData()
    } catch (error) {
      toastService.showError(t('error_deleting'))
    } finally {
      setShowConfirmDialog(false)
      setObjectToDelete(null)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedObjects.size === 0) return

    try {
      const deletePromises = Array.from(selectedObjects).map((id) => service.delete({ id }))
      await Promise.all(deletePromises)
      toastService.showInfo(t('successfully_deleted_multiple', { count: selectedObjects.size }))
      clearSelection()
      loadData()
    } catch (error) {
      toastService.showError(t('error_bulk_deleting'))
    } finally {
      setShowBulkDeleteDialog(false)
    }
  }

  const handleEditSuccess = () => {
    setShowEditDialog(false)
    loadData()
    const objectName = editingObject?.name || typeName
    toastService.showSuccess(
      t(isCreating ? 'successfully_created' : 'successfully_updated', {
        name: objectName,
      })
    )
  }

  const handleEditError = (_error: any) => {
    toastService.showError(t('error_saving'))
  }

  const getDocumentCount = (object: any): number => {
    return (object as any).document_count ?? (unfilteredData.find((d: any) => d.id === (object as any).id) as any)?.document_count ?? 0
  }

  const getMatching = (object: any): string => {
    const o = object as any
    if (o.matching_algorithm === MATCH_AUTO) {
      return t('automatic')
    } else if (o.matching_algorithm === MATCH_NONE) {
      return t('none')
    } else if (o.match && o.match.length > 0) {
      const algorithms = getMatchingAlgorithms(t)
      const algorithm = algorithms.find((a) => a.value === o.matching_algorithm)
      if (algorithm) {
        const shortName = algorithm.label.split(':')[0].trim()
        return `${shortName}: ${o.match}`
      }
    }
    return '-'
  }

  const showInitialSpinner = loading && initialLoad

  if (showInitialSpinner) {
    return (
      <div className="management-list">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
          <p className="mt-2">{t('loading_data')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="management-list">
      <PageHeader title={t(typeNamePlural)} info={info} infoLink={infoLink}>
        {typeof headerActions === 'function' ? headerActions(selectedObjects) : headerActions}
        {!hidePermissionsButton && (
          resolvedPermissionType && <IfPermissions action={PermissionAction.Change} type={resolvedPermissionType}>
            <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={openPermissionsDialog} disabled={selectedObjects.size === 0}>
              <i className="bi bi-person-fill-lock"></i>&nbsp;{t('permissions')}
            </button>
          </IfPermissions>
        )}
        {resolvedPermissionType && <IfPermissions action={PermissionAction.Delete} type={resolvedPermissionType}>
          <button type="button" className="btn btn-sm btn-outline-danger me-2" onClick={openBulkDeleteDialog} disabled={selectedObjects.size === 0}>
            <i className="bi bi-trash"></i>&nbsp;{t('delete')}
          </button>
        </IfPermissions>}
        {selectedObjects.size > 0 && (
          <button className="btn btn-sm btn-outline-secondary me-2" onClick={clearSelection}>
            <i className="bi bi-x"></i>&nbsp;{t('clear_selection')}
          </button>
        )}
        {resolvedPermissionType && <IfPermissions action={PermissionAction.Add} type={resolvedPermissionType}>
          <button type="button" className="btn btn-sm btn-primary" onClick={openCreateDialog}>
            <i className="bi bi-plus-circle"></i>&nbsp;{t('create')}
          </button>
        </IfPermissions>}
      </PageHeader>

      <div className="row mb-3">
        <div className="col-md mb-2 mb-xl-0">
          <div className="form-inline d-flex align-items-center">
            <label className="text-muted me-2 mb-0">{t('filter_by')}:</label>
            <input
              className="form-control form-control-sm flex-fill w-auto"
              type="text"
              ref={filterInputRef}
              value={nameFilterInput}
              onChange={(e) => handleNameFilterChange(e.target.value)}
              onFocus={() => {
                maintainFilterFocusRef.current = true
              }}
              onBlur={() => {
                maintainFilterFocusRef.current = false
              }}
              placeholder={t('name')}
            />
          </div>
        </div>
        {shouldShowPagination && (
          <div className="col-auto d-none d-md-block">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} alwaysVisible />
          </div>
        )}
      </div>

      <div className="position-relative">
        {loading && !initialLoad && (
          <div className="management-list__table-overlay">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        )}

        <ManagementTable
          data={data}
          extraColumns={extraColumns}
          sortField={sortField}
          sortReverse={sortReverse}
          onSort={handleSort}
          selectedObjects={selectedObjects}
          toggleAll={toggleAll}
          onToggleAll={handleToggleAll}
          onToggleSelected={handleToggleSelected}
          onFilterDocuments={handleFilterDocuments}
          onEdit={openEditDialog}
          onDelete={openDeleteDialog}
          onClone={resolvedPermissionType === PermissionType.CustomField || resolvedPermissionType === PermissionType.DocumentType ? openCloneDialog : undefined}
          onExtraAction={onExtraAction}
          extraActionTitle={extraActionTitle}
          extraActionIcon={extraActionIcon}
          getDocumentCount={getDocumentCount}
          documentCountValueFn={documentCountValueFn}
          isDocumentCountDisabled={isDocumentCountDisabled}
          getMatching={getMatching}
          permissionType={resolvedPermissionType}
          t={t}
          emptyLabel={t(typeNamePlural)}
          hideMatchingColumn={hideMatchingColumn}
        />
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted small">
          {collectionSize > 0 ? `${rangeStart} - ${rangeEnd} / ${collectionSize}` : '0 / 0'}
          {selectedObjects.size > 0 && (
            <span className="ms-2">
              ({selectedObjects.size} {t('selected')})
            </span>
          )}
        </div>
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} alwaysVisible />
      </div>

      {showEditDialog && (
        <EditDialogComponent
          object={editingObject}
          isCreating={isCreating}
          onSuccess={handleEditSuccess}
          onError={handleEditError}
          onClose={() => setShowEditDialog(false)}
        />
      )}

      {showConfirmDialog && (
        <div className="modal show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('confirm_delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowConfirmDialog(false)}></button>
              </div>
              <div className="modal-body">{objectToDelete ? getDeleteMessage(objectToDelete) : ''}</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfirmDialog(false)}>
                  {t('cancel')}
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDelete}>
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteDialog && (
        <div className="modal show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('confirm_bulk_delete')} ({selectedObjects.size})
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowBulkDeleteDialog(false)}></button>
              </div>
              <div className="modal-body">
                <p>{t('bulk_delete_warning')}</p>
                <p className="text-muted">{t('bulk_delete_irreversible')}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkDeleteDialog(false)}>
                  {t('cancel')}
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!hidePermissionsButton && (
        <PermissionsDialog
          show={showPermissionsDialog}
          onHide={() => setShowPermissionsDialog(false)}
          onConfirm={handlePermissionsConfirm}
          selectedCount={selectedObjects.size}
        />
      )}
    </div>
  )
}
